import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import consoleApi, { errorMessage } from "./consoleApi";
import { CARD, staffTone, staffVerb } from "./format";
import { ActivityIcon } from "./icons";

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

export default function StaffActivity() {
  const [entries, setEntries] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hideSignIns, setHideSignIns] = useState(true);

  useEffect(() => {
    consoleApi.get("/activity", { params: { limit: 100 } })
      .then((res) => { setEntries(res.data.data); setNextCursor(res.data.nextCursor); })
      .catch((err) => setError(errorMessage(err, "Couldn't load staff activity.")))
      .finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await consoleApi.get("/activity", { params: { cursor: nextCursor, limit: 100 } });
      setEntries((list) => [...list, ...res.data.data]);
      setNextCursor(res.data.nextCursor);
    } catch (err) {
      setError(errorMessage(err, "Couldn't load more."));
    } finally {
      setLoadingMore(false);
    }
  };

  const days = useMemo(() => {
    const groups = [];
    for (const e of entries) {
      if (hideSignIns && e.action === "SIGNED_IN") continue;
      const key = new Date(e.createdAt).toDateString();
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.entries.push(e);
      else groups.push({ key, label: dayLabel(e.createdAt), entries: [e] });
    }
    return groups;
  }, [entries, hideSignIns]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Staff activity</h1>
          <p className="mt-1 text-sm text-slate-500">Every approval, rejection and suspension, and who signed in to the console.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={hideSignIns} onChange={(e) => setHideSignIns(e.target.checked)} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
          Hide sign-ins
        </label>
      </div>

      <div className={CARD}>
        {loading ? (
          <div className="space-y-3 p-6">
            {[0, 1, 2].map((i) => <div key={i} className="h-8 animate-pulse rounded-lg bg-slate-100" />)}
          </div>
        ) : error && entries.length === 0 ? (
          <p className="p-6 text-sm text-red-600">{error}</p>
        ) : days.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <ActivityIcon className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-700">Nothing yet</p>
            <p className="mt-1 text-sm text-slate-500">Approvals, rejections and suspensions will show up here.</p>
          </div>
        ) : (
          days.map((day) => (
            <section key={day.key} aria-label={day.label} className="px-6 py-5 [&+&]:border-t [&+&]:border-slate-100">
              <h2 className="mb-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">{day.label}</h2>
              <ol className="relative space-y-4 border-l border-slate-200 pl-5">
                {day.entries.map((e) => {
                  const name = e.details?.companyName;
                  const linkable = e.tenantId && e.action !== "TENANT_REJECTED";
                  return (
                    <li key={e.id} className="relative text-sm">
                      <span className={`absolute -left-[25px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-white ${staffTone(e.action)}`} />
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                        <p className="text-slate-700">
                          <span className="font-semibold text-slate-900">{e.staffEmail}</span> {staffVerb(e.action)}
                          {name && (
                            <>
                              {" "}
                              {linkable ? (
                                <Link to={`/tenants/${e.tenantId}`} className="font-semibold text-indigo-700 hover:underline">{name}</Link>
                              ) : (
                                <span className="font-semibold text-slate-900">{name}</span>
                              )}
                            </>
                          )}
                        </p>
                        <time dateTime={e.createdAt} className="text-xs tabular-nums text-slate-400">
                          {new Date(e.createdAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                        </time>
                      </div>
                      {e.details?.reason && <p className="mt-1 rounded-lg bg-slate-50 px-3 py-1.5 text-xs text-slate-600">“{e.details.reason}”</p>}
                    </li>
                  );
                })}
              </ol>
            </section>
          ))
        )}
      </div>

      {nextCursor && !loading && (
        <div className="text-center">
          <button onClick={loadMore} disabled={loadingMore} className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {loadingMore ? "Loading…" : "Load older"}
          </button>
        </div>
      )}
      {error && entries.length > 0 && <p className="text-center text-sm text-red-600">{error}</p>}
    </div>
  );
}
